import {
  type DomainInvalidReason,
  type DisasterRecoveryProposalActionValue,
  type ProposalValidity,
} from '@mls-wallet/shared-types';

/** Raw proposal shape returned by the sync server (dates are ISO strings over the wire). */
export type ServerDisasterRecoveryProposal = {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  validity: ProposalValidity;
  domainInvalidReason?: DomainInvalidReason;
  action: DisasterRecoveryProposalActionValue;
  organizationId: string;
  createdByUserId: string;
  salt: string;
  expirationTimestamp: number;
  recoveryAddress: string | null;
  timelockDurationSeconds: number | null;
  disasterRecoveryId: string | null;
  executedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdByUser: {
    id: string;
    name: string;
  };
  alreadyExists?: boolean;
};

/** Client-friendly mapped shape derived from {@link ServerDisasterRecoveryProposal}. */
export type DisasterRecoveryProposal = {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  validity: ProposalValidity;
  domainInvalidReason?: DomainInvalidReason;
  organizationId: string;
  createdByUserId: string;
  createdByUserName?: string;
  recoveryWalletAddress: string;
  timelockDurationSeconds: number;
  createdAt: string;
  updatedAt: string;
};

/**
 * Maps a raw server INITIATE proposal to the client {@link DisasterRecoveryProposal} shape.
 * Throws if the required `recoveryAddress` or `timelockDurationSeconds` fields are absent,
 * since those are mandatory for INITIATE proposals.
 */
export function mapInitiateProposal(
  proposal: ServerDisasterRecoveryProposal
): DisasterRecoveryProposal {
  if (proposal.recoveryAddress === null) {
    throw new Error(
      `INITIATE disaster recovery proposal ${proposal.id} is missing recoveryAddress`
    );
  }
  if (proposal.timelockDurationSeconds === null) {
    throw new Error(
      `INITIATE disaster recovery proposal ${proposal.id} is missing timelockDurationSeconds`
    );
  }

  return {
    id: proposal.id,
    status: proposal.status,
    validity: proposal.validity,
    ...(proposal.domainInvalidReason && {
      domainInvalidReason: proposal.domainInvalidReason,
    }),
    organizationId: proposal.organizationId,
    createdByUserId: proposal.createdByUserId,
    createdByUserName: proposal.createdByUser.name,
    recoveryWalletAddress: proposal.recoveryAddress,
    timelockDurationSeconds: proposal.timelockDurationSeconds,
    createdAt: proposal.createdAt,
    updatedAt: proposal.updatedAt,
  };
}
